import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  DragEvent,
  ClipboardEvent,
  type CSSProperties,
} from 'react';
import { useIntl } from 'react-intl';

import {
  _mergeProps,
  _useAriaLabelingProps as useAriaLabelingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { expressionCSS } from './DateTimeInput.sty.js';
import { useDateTimeInput } from './hooks/useDateTimeInput.js';
import type { DateTimeInputProps, DateTimeInputRef } from './types.js';
import { visuallyHiddenCSS } from '../../core/components/visually-hidden/VisuallyHidden.sty.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { dateTimePickerSpinButtonGroupCSS } from '../date-time-picker/DateTimePicker.sty.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import { SpinButtonGroup } from '../spin-buttons/spin-button-group/SpinButtonGroup.js';

function preventDefault(e: DragEvent | ClipboardEvent) {
  e.preventDefault();
}

/** DateTimeRangePicker component
 * @internal
 */
export const DateTimeInput = forwardRef<DateTimeInputRef, DateTimeInputProps>(
  (props, forwardedRef) => {
    const rootRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const expressionInputRef = useRef<HTMLSpanElement>(null);
    const intl = useIntl();
    const {
      ariaProps: ariaFormProps,
      onChange: validationOnChange,
      onBlur,
      onInvalid,
    } = useFormFieldValidation(inputRef);

    const {
      disabled,
      readOnly,
      placeholder: propPlaceholder,
      className: consumerClassName,
      style: consumerStyle,
      name,
      'data-testid': dataTestId,
      'aria-disabled': ariaDisabledProp,
      controlId,
      required,
      type = 'datetime',
      controlState,
    }: DateTimeInputProps = props;

    const dateTimePlaceholder = intl.formatMessage({
      defaultMessage: 'Date time',
      id: '3BkbHvGCQ51HRbQ0',
      description: 'Placeholder for the date time input',
    });
    const datePlaceholder = intl.formatMessage({
      defaultMessage: 'Date',
      id: 'iOw//K55EJMc00O4',
      description: 'Placeholder for the date input',
    });
    const timePlaceholder = intl.formatMessage({
      defaultMessage: 'Time',
      id: 'QCPgO1L4QJB9hMGs',
      description: 'Placeholder for the time input',
    });

    let placeholder = propPlaceholder;
    if (!propPlaceholder) {
      switch (type) {
        case 'datetime':
          placeholder = dateTimePlaceholder;
          break;
        case 'date':
          placeholder = datePlaceholder;
          break;
        case 'time':
          placeholder = timePlaceholder;
          break;
      }
    }

    // Backwards compatibility for controlState.
    const [hasFormControlError, hint] = useFormControlState(controlState);
    // _useInputHint will be replaced with new validation concept. APPDEV-8298
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const { inputProps } = useInputHint({
      hint,
      hasError: hasFormControlError,
    });

    const ariaLabelingProps = useAriaLabelingProps(props);

    const {
      expressionInputHandler,
      expressionOnFocusHandler,
      expressionOnBlurHandler,
      expressionOnKeyDownHandler,
      onCopyCapture,
      onPaste,
      value,
      valueType,
      spinButtons,
      spinButtonHandlers,
      inputValue,
      focus,
      validateTimeValue,
    } = useDateTimeInput(props, expressionInputRef);

    const isExpressionInput = valueType === 'expression';

    const [isEditable, setIsEditable] = useState(false);

    const { dispatch: formMessageDispatch } = useFormFieldMessagesContext();

    useImperativeHandle(
      forwardedRef,
      (): DateTimeInputRef => ({
        element: rootRef.current,
        inputRef: inputRef.current,
        expressionInputRef: expressionInputRef.current,
        spinButtonHandlers,
        focus,
        validate() {
          validateTimeValue(
            value,
            expressionInputRef.current?.textContent ?? undefined,
          );
          formMessageDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
        },
      }),
      [
        focus,
        formMessageDispatch,
        spinButtonHandlers,
        validateTimeValue,
        value,
      ],
    );

    const expressionLabel = intl.formatMessage(
      {
        defaultMessage: '{ariaLabelPrefix} expression',
        id: 'M6eNgatb2wf4fQ3I',
        description: 'Textbox to enter a dql timeframe expression.',
      },
      {
        ariaLabelPrefix: ariaLabelingProps['aria-label'],
      },
    );

    useEffect(() => {
      validationOnChange(); // needs to be in an effect here because the value update should happen before the validation, which validates the input ref value
      // validationOnChange doesn't change and can be excluded
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);

    return (
      <Flex
        ref={rootRef}
        as="span"
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        onCopyCapture={onCopyCapture}
        onPaste={onPaste}
        gap={0}
      >
        <VisuallyHidden>
          <input
            id={controlId}
            ref={inputRef}
            name={name}
            value={inputValue}
            onChange={() => {
              /** noop */
            }}
            onFocus={focus}
            onBlur={onBlur}
            onInvalid={onInvalid}
            disabled={disabled}
            tabIndex={-1}
            required={required}
            readOnly={readOnly}
            aria-disabled={ariaDisabledProp}
            {..._mergeProps(
              // Backwards compatibility for controlState.
              controlState ? inputProps : ariaFormProps,
              ariaLabelingProps,
            )}
          />
        </VisuallyHidden>
        <span
          className={clsx(expressionCSS, {
            [visuallyHiddenCSS]: !isExpressionInput,
          })}
          role="textbox"
          spellCheck={false}
          tabIndex={disabled || !isExpressionInput ? -1 : 0}
          style={
            {
              '--data-placeholder': `"${placeholder}"`,
            } as CSSProperties
          }
          ref={expressionInputRef}
          contentEditable={
            !disabled &&
            !readOnly &&
            // contentEditable when focused and non-editable when blurred, allowing text selection.
            (isEditable ||
              value === null ||
              value === undefined ||
              value?.value === '')
          }
          onInput={expressionInputHandler}
          onFocus={(e) => {
            setIsEditable(true);
            expressionOnFocusHandler(e);
          }}
          onBlur={(e) => {
            expressionOnBlurHandler(e);
            setIsEditable(false);
          }}
          onKeyDown={expressionOnKeyDownHandler}
          onPaste={preventDefault}
          onDragOver={preventDefault}
          onDrop={preventDefault}
          aria-label={expressionLabel}
        />
        <SpinButtonGroup
          className={clsx(dateTimePickerSpinButtonGroupCSS, {
            [visuallyHiddenCSS]: isExpressionInput,
          })}
          valueOverflow
        >
          {spinButtons}
        </SpinButtonGroup>
      </Flex>
    );
  },
);
